"use client";

import type { ReactNode } from "react";
import React, { Suspense, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { IntlProvider } from "react-intl";
import { ErrorFallback, Spinner } from "@akashnetwork/ui/components";
import { cn } from "@akashnetwork/ui/utils";
import { ACCOUNT_BAR_HEIGHT } from "@src/config/ui.config";
import { useOnboardingChrome } from "@src/hooks/useOnboardingChrome";
import { useTopBanner } from "@src/hooks/useTopBanner";
import { getBrowserLocale } from "@src/lib/browser-locale/browser-locale";
import { LinearLoadingSkeleton } from "../shared/LinearLoadingSkeleton";
import { TopNav } from "./TopNav/TopNav";
import { TrackingScripts } from "./TrackingScripts";
export const DEPENDENCIES = {
  LinearLoadingSkeleton,
  TopNav,
  TrackingScripts,
  useOnboardingChrome,
  useTopBanner
};
type Props = {
  isLoading?: boolean;
  disableContainer?: boolean;
  containerClassName?: string;
  background?: "default" | "white" | "dots";
  children?: ReactNode;
  dependencies?: typeof DEPENDENCIES;
};
const Layout: React.FunctionComponent<Props> = ({
  children,
  isLoading,
  disableContainer,
  containerClassName,
  background,
  dependencies
}) => {
  const [locale, setLocale] = useState("en-US");
  useEffect(function adoptBrowserLocale() {
    setLocale(getBrowserLocale());
  }, []);
  return <IntlProvider locale={locale} defaultLocale="en-US" data-sentry-element="IntlProvider" data-sentry-component="Layout" data-sentry-source-file="Layout.tsx">
      <LayoutApp isLoading={isLoading} disableContainer={disableContainer} containerClassName={containerClassName} background={background} dependencies={dependencies} data-sentry-element="LayoutApp" data-sentry-source-file="Layout.tsx">
        {children}
      </LayoutApp>
    </IntlProvider>;
};
const LayoutApp: React.FunctionComponent<Props> = ({
  children,
  isLoading = false,
  disableContainer,
  containerClassName = "",
  background = "default",
  dependencies: d = DEPENDENCIES
}) => {
  const {
    LinearLoadingSkeleton,
    TopNav,
    TrackingScripts,
    useOnboardingChrome,
    useTopBanner
  } = d;
  const {
    hasBanner
  } = useTopBanner();
  const {
    isStripped
  } = useOnboardingChrome();
  return <div data-background={background} className="relative flex min-h-dvh flex-col overflow-x-clip data-[background=white]:bg-white data-[background=white]:text-foreground data-[background=dots]:bg-dots dark:data-[background=white]:bg-background" data-sentry-component="LayoutApp" data-sentry-source-file="Layout.tsx">
      <div className="w-full flex-1" style={{
      marginTop: `var(--app-header-height, ${ACCOUNT_BAR_HEIGHT + (hasBanner ? 40 : 0)}px)`
    }}>
        <TopNav minimal={isStripped} data-sentry-element="TopNav" data-sentry-source-file="Layout.tsx" />

        <LinearLoadingSkeleton isLoading={isLoading} data-sentry-element="LinearLoadingSkeleton" data-sentry-source-file="Layout.tsx" />

        <ErrorBoundary FallbackComponent={ErrorFallback} data-sentry-element="ErrorBoundary" data-sentry-source-file="Layout.tsx">
          <div className={cn({
          ["container p-6 pb-8"]: !disableContainer
        }, containerClassName)}>{children}</div>
        </ErrorBoundary>
      </div>

      <Suspense fallback={null} data-sentry-element="Suspense" data-sentry-source-file="Layout.tsx">
        <TrackingScripts data-sentry-element="TrackingScripts" data-sentry-source-file="Layout.tsx" />
      </Suspense>
    </div>;
};
export const Loading: React.FunctionComponent<{
  text?: string;
  testId?: string;
}> = ({
  text,
  testId
}) => {
  return <div className="flex h-full w-full flex-col items-center justify-center pb-12 pt-12" data-testid={testId} data-sentry-component="Loading" data-sentry-source-file="Layout.tsx">
      <Spinner size="large" data-sentry-element="Spinner" data-sentry-source-file="Layout.tsx" />
      {text && <h5 className="pt-4">{text}</h5>}
    </div>;
};
export default Layout;